import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Wraps page-level components. Catches render errors so a single broken page
 * does not take down the entire app. Without this, any throw in a page's
 * render, memo, or effect propagates to the root and produces a blank screen.
 */
export class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  override componentDidCatch(error: unknown, info: { componentStack?: string | null }): void {
    console.error('[PageErrorBoundary]', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ padding: 'var(--gap-l)', maxWidth: 480, margin: '0 auto' }}>
        <p className="label" style={{ color: 'var(--neg)', marginBottom: 'var(--gap-s)' }}>
          {this.props.pageName ?? 'This page'} encountered an error.
        </p>
        <p className="mono faint" style={{ fontSize: 'var(--fs-micro)', marginBottom: 'var(--gap-m)' }}>
          {this.state.message}
        </p>
        <button
          className="btn-secondary"
          onClick={() => this.setState({ hasError: false, message: '' })}
        >
          Retry
        </button>
      </div>
    );
  }
}
